// Datenschutz-Filter der Frontend-Fehlerüberwachung (Bugsink speichert Events
// unverändert). Reine Funktionen ohne SDK-Import, damit sie ohne Browser
// testbar sind (tests/javascript/errorTracking.test.mjs).
//
// Was ein Event behalten darf: Fehlermeldung und Stacktrace, URL ohne Query
// und ohne Tokens im Pfad, User-Agent, Pfad des Referers, Benutzer höchstens
// als numerische ID. Gleiche Regeln wie im Backend (App\Monitoring\SentryBeforeSend).
// In Fehlermeldungen werden nur E-Mails, Query-Strings, geheime Pfadsegmente
// und MySQL "Duplicate entry" maskiert: Meldungen ohne Personendaten bauen.

const FILTERED = '[Filtered]';
const EMAIL_PLACEHOLDER = '[email]';

// Höchstens so viele Events pro Seitenaufruf, damit eine Fehlerschleife im
// Konfigurator Bugsink nicht flutet.
export const MAX_EVENTS_PER_PAGE = 5;

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

// Pfadsegmente mit Geheimnissen: Passwort-Reset, Registrierungsbestätigung,
// TÜR24-Freigabe per Mail-Link, Report-Schlüssel.
const SECRET_PATH_PATTERN = /(\/reset-password\/reset\/|\/registration\/confirm\/|\/tuer24\/order\/|\/report\/(?:view|subscribe|unsubscribe)\/)[^/?#\s"']+/g;

// Query-String und Fragment in URLs innerhalb von Freitext
const URL_QUERY_PATTERN = /(\bhttps?:\/\/[^\s?#"'<>]*)[?#][^\s"'<>]*/gi;

// MySQL/MariaDB nennen den doppelten Wert im Klartext, oft Benutzer-,
// Firmennamen oder E-Mails. Ohne "' for key" (gekürzte Meldung) bis zum Ende.
const DUPLICATE_ENTRY_PATTERN = /(Duplicate entry ')[\s\S]*?(?=' for key|$)/g;

// Klick- und Eingabe-Breadcrumbs beschreiben das Element als CSS-Pfad, das SDK
// hängt aria-label, type, name, title und alt samt Wert an, z. B.
// button.btn[title="Kunde Max Muster bearbeiten"]. Die Werte sind Freitext
// (Namen, Dateinamen), Tag, #id und .class reichen zur Orientierung. Werte
// sind nicht escaped, deshalb endet ein Attribut erst vor dem nächsten
// Attribut, dem Trenner " > " oder dem Ende.
const DOM_BREADCRUMB_CATEGORIES = ['ui.click', 'ui.input'];
const DOM_ATTRIBUTE_PATTERN = /\[[\w:-]+="[\s\S]*?"\](?=\[[\w:-]+="| > |$)/g;

// Rauschen, das nichts über unseren Code aussagt: Netzwerkabbrüche,
// abgebrochene Requests, ResizeObserver-Warnungen.
export const IGNORE_ERRORS = [
  /ResizeObserver loop/i,
  /^(TypeError: )?Failed to fetch/i,
  /^(TypeError: )?NetworkError when attempting to fetch resource/i,
  /^(TypeError: )?Load failed$/i,
  /^(Error: )?Network Error$/i, // axios ohne Antwort
  /^(Error: )?Request aborted$/i, // axios ECONNABORTED
  /^(CanceledError: |Cancel: )?canceled$/i, // axios abgebrochen
  /AbortError/i,
  /The (user aborted a request|operation was aborted)/i,
  // tourguidejs lehnt Klicks während eines Schrittwechsels so ab, seine
  // eigenen Knöpfe fangen das nicht (Bugsink MYBRUNEXCH-15); der Klick ist
  // dann einfach wirkungslos
  /^(Non-Error promise rejection captured with value: )?Promise waiting$/,
  // Swal-Dialog per Esc, Klick daneben oder Abbrechen geschlossen (v7-Shim
  // assets/swal-legacy.js lehnt mit dem Grund ab)
  /^Non-Error promise rejection captured with value: (backdrop|esc|close|cancel|timer)$/,
];

// Firefox bricht beim Wegnavigieren laufende Requests und Chunk-Ladevorgänge
// sofort ab und meldet sie als Fehler (Bugsink MYBRUNEXCH-6: ChunkLoadError
// 36 ms nach dem Klick auf einen Menülink). Was nach beforeunload oder
// pagehide passiert, sagt nichts über unseren Code. Bricht ein Dialog das
// Verlassen ab, gilt die Seite nach resetAfter Millisekunden wieder als aktiv.
export function createLeaveGuard(target, { resetAfter = 5000, now = Date.now } = {}) {
  let leftAt = null;
  const leave = () => {
    leftAt = now();
  };
  target.addEventListener('beforeunload', leave);
  target.addEventListener('pagehide', leave);
  // Zurück aus dem bfcache
  target.addEventListener('pageshow', () => {
    leftAt = null;
  });

  return () => leftAt !== null && now() - leftAt < resetAfter;
}

// Fehler aus Browser-Erweiterungen
export const DENY_URLS = [
  /^(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//i,
  /^chrome:\/\//i,
];

export function scrubText(text) {
  if (typeof text !== 'string') {
    return text;
  }

  return text
    .replace(DUPLICATE_ENTRY_PATTERN, `$1${FILTERED}`)
    .replace(EMAIL_PATTERN, EMAIL_PLACEHOLDER)
    .replace(URL_QUERY_PATTERN, `$1?${FILTERED}`)
    .replace(SECRET_PATH_PATTERN, `$1${FILTERED}`);
}

// Funktioniert für absolute URLs und reine Pfade.
export function scrubUrl(url) {
  if (typeof url !== 'string') {
    return url;
  }

  return url
    .split(/[?#]/, 1)[0]
    .replace(EMAIL_PATTERN, EMAIL_PLACEHOLDER)
    .replace(SECRET_PATH_PATTERN, `$1${FILTERED}`);
}

function refererPath(referer) {
  try {
    return scrubUrl(new URL(referer).pathname);
  } catch {
    return undefined;
  }
}

function scrubValue(value, depth = 0) {
  if (typeof value === 'string') {
    return scrubText(value);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (depth >= 5) {
    return FILTERED;
  }

  const copy = Array.isArray(value) ? [] : {};
  Object.keys(value).forEach((key) => {
    copy[key] = scrubValue(value[key], depth + 1);
  });

  return copy;
}

// Fehler aus Inline-Skripten und eval tragen die Seiten-URL samt Query als
// Dateinamen. Bundle-URLs (/build/…) haben keine Query und bleiben gleich, die
// Source-Map-Zuordnung über debug_meta also auch.
function scrubStacktrace(stacktrace) {
  (stacktrace?.frames || []).forEach((frame) => {
    delete frame.vars;
    ['filename', 'abs_path'].forEach((key) => {
      if (typeof frame[key] === 'string') {
        frame[key] = scrubUrl(frame[key]);
      }
    });
  });
}

function scrubRequest(request) {
  const headers = request.headers || {};
  const scrubbed = {};

  if (request.url) {
    scrubbed.url = scrubUrl(request.url);
  }
  if (request.method) {
    scrubbed.method = request.method;
  }

  const allowedHeaders = {};
  Object.keys(headers).forEach((name) => {
    const lower = name.toLowerCase();
    if (lower === 'user-agent') {
      allowedHeaders['User-Agent'] = headers[name];
    } else if (lower === 'referer') {
      const path = refererPath(headers[name]);
      if (path) {
        allowedHeaders.Referer = path;
      }
    }
  });
  if (Object.keys(allowedHeaders).length > 0) {
    scrubbed.headers = allowedHeaders;
  }

  return scrubbed;
}

export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb || breadcrumb.category === 'console') {
    return null;
  }

  const scrubbed = { ...breadcrumb };
  if (scrubbed.message) {
    const message = DOM_BREADCRUMB_CATEGORIES.includes(scrubbed.category)
      ? scrubbed.message.replace(DOM_ATTRIBUTE_PATTERN, '')
      : scrubbed.message;
    scrubbed.message = scrubText(message);
  }
  if (scrubbed.data) {
    scrubbed.data = { ...scrubbed.data };
    ['url', 'from', 'to'].forEach((key) => {
      if (typeof scrubbed.data[key] === 'string') {
        scrubbed.data[key] = scrubUrl(scrubbed.data[key]);
      }
    });
  }

  return scrubbed;
}

/**
 * Bereinigt ein Event in place. userId kommt aus der Seitenkonfiguration
 * (nie aus window.user, das E-Mail und Namen enthält).
 */
export function scrubEvent(event, { userId = null } = {}) {
  if (userId !== null && userId !== undefined && /^\d+$/.test(String(userId))) {
    event.user = { id: String(userId) };
  } else {
    delete event.user;
  }

  if (event.request) {
    event.request = scrubRequest(event.request);
  }

  if (event.message) {
    event.message = scrubText(event.message);
  }

  (event.exception?.values || []).forEach((exception) => {
    exception.value = scrubText(exception.value);
    scrubStacktrace(exception.stacktrace);
  });
  scrubStacktrace(event.stacktrace);
  (event.threads?.values || []).forEach((thread) => scrubStacktrace(thread.stacktrace));
  // code_file muss gleich bereinigt sein wie abs_path/filename der Frames,
  // sonst findet Bugsink die Source Map nicht mehr.
  (event.debug_meta?.images || []).forEach((image) => {
    if (typeof image.code_file === 'string') {
      image.code_file = scrubUrl(image.code_file);
    }
  });

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
  }

  if (event.extra) {
    // Das SDK legt geworfene Nicht-Error-Werte (throw {…}, reject(responseData))
    // komplett als __serialized__ ab und die Argumente abgefangener Callbacks
    // (DOM-Events samt detail, setTimeout-Argumente) als arguments. Beides
    // kann beliebige Kundendaten enthalten; die Schlüssel des geworfenen
    // Objekts stehen ohnehin in der Fehlermeldung.
    const extra = { ...event.extra };
    delete extra.__serialized__;
    delete extra.arguments;
    if (Object.keys(extra).length > 0) {
      event.extra = scrubValue(extra);
    } else {
      delete event.extra;
    }
  }

  return event;
}

// Zähler pro Seitenaufruf: liefert true, solange noch Events erlaubt sind.
export function createEventBudget(max = MAX_EVENTS_PER_PAGE) {
  let sent = 0;

  return () => {
    if (sent >= max) {
      return false;
    }
    sent += 1;

    return true;
  };
}
